import React, { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Paper,
  Chip,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Rating,
  Avatar,
  Stack,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as ShoppingCartIcon,
  ExpandMore as ExpandMoreIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../store/store";
import {
  fetchProductBySlug,
  clearCurrentProduct,
} from "../store/slices/productSlice";
import { addItemLocally } from "../store/slices/cartSlice";
import { Review, ReviewSummary } from "../types";
import api from "../services/api";

const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentProduct, isLoading, error } = useSelector(
    (state: RootState) => state.product
  );

  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary | null>(
    null
  );
  const [reviewsLoading, setReviewsLoading] = useState(false);

  useEffect(() => {
    if (slug) {
      dispatch(fetchProductBySlug(slug));
    }
    return () => {
      dispatch(clearCurrentProduct());
    };
  }, [dispatch, slug]);

  const handleQuantityChange = (newQuantity: number) => {
    if (
      newQuantity >= 1 &&
      currentProduct &&
      newQuantity <= currentProduct.stockQuantity
    ) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (currentProduct) {
      dispatch(addItemLocally({ product: currentProduct, quantity }));
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 3000);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Reviews 탭 클릭 시 리뷰 데이터 로드
    if (newValue === 1 && currentProduct && reviews.length === 0) {
      fetchReviews(currentProduct.id);
    }
  };

  const fetchReviews = async (productId: number) => {
    setReviewsLoading(true);
    try {
      // 실제 API 호출
      const response = await api.get(`/products/${productId}/reviews`);
      const reviewsData = response.data.content || []; // Spring Boot Page 구조

      // Review 데이터 매핑 (백엔드 ReviewDTO -> 프론트엔드 Review)
      const mappedReviews: Review[] = reviewsData.map((review: any) => ({
        id: review.id,
        productId: productId,
        userId: review.userId || 0,
        userName: review.userName,
        rating: review.rating,
        title: "", // ReviewDTO에 title 필드가 없으므로 빈 값
        comment: review.comment,
        verified: review.verified,
        createdAt: review.createdAt
      }));

      // ReviewSummary 계산
      const totalReviews = mappedReviews.length;
      if (totalReviews > 0) {
        const averageRating = mappedReviews.reduce((sum, review) => sum + review.rating, 0) / totalReviews;

        // 별점 분포 계산
        const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        mappedReviews.forEach(review => {
          ratingDistribution[review.rating as keyof typeof ratingDistribution]++;
        });

        const calculatedSummary: ReviewSummary = {
          totalReviews,
          averageRating: Math.round(averageRating * 10) / 10, // 소수점 첫째자리까지
          ratingDistribution
        };

        setReviews(mappedReviews);
        setReviewSummary(calculatedSummary);
      } else {
        setReviews([]);
        setReviewSummary(null);
      }
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
      // 에러 발생 시 빈 배열로 설정
      setReviews([]);
      setReviewSummary(null);
    } finally {
      setReviewsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !currentProduct) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">Product not found</Alert>
        <Button onClick={() => navigate("/products")} sx={{ mt: 2 }}>
          Back to Products
        </Button>
      </Container>
    );
  }

  const isOutOfStock = currentProduct.stockQuantity === 0;

  // TabPanel 컴포넌트
  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

  function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`product-tabpanel-${index}`}
        aria-labelledby={`product-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }

  function a11yProps(index: number) {
    return {
      id: `product-tab-${index}`,
      "aria-controls": `product-tabpanel-${index}`,
    };
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {addedToCart && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Product added to cart successfully!
        </Alert>
      )}

      <Grid container spacing={6}>
        {/* Product Image */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            component="img"
            src={
              currentProduct.imageUrl ||
              "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600"
            }
            alt={currentProduct.name}
            sx={{
              width: "100%",
              height: 500,
              objectFit: "cover",
              borderRadius: 2,
            }}
          />
        </Grid>

        {/* Product Info */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box>
            {/* Breadcrumb */}
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <span
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/products")}
              >
                Products
              </span>
              {" > "}
              {currentProduct.categoryName}
            </Typography>

            {/* Product Name */}
            <Typography variant="h3" component="h1" gutterBottom>
              {currentProduct.name}
            </Typography>

            {/* Price */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography variant="h4" color="primary">
                ₱{currentProduct.price.toLocaleString()}
              </Typography>
              {currentProduct.comparePrice && (
                <Typography
                  variant="h5"
                  sx={{ textDecoration: "line-through", color: "grey.500" }}
                >
                  ₱{currentProduct.comparePrice.toLocaleString()}
                </Typography>
              )}
            </Box>

            {/* Stock Status */}
            <Box sx={{ mb: 3 }}>
              {isOutOfStock ? (
                <Chip label="Out of Stock" color="error" />
              ) : (
                <Chip
                  label={`${currentProduct.stockQuantity} in stock`}
                  color="success"
                />
              )}
            </Box>

            {/* Description */}
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
              {currentProduct.description}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Product Details */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Product Details
              </Typography>
              <Grid container spacing={2}>
                {currentProduct.brand && (
                  <Grid size={6}>
                    <Typography variant="body2" color="text.secondary">
                      Brand: {currentProduct.brand}
                    </Typography>
                  </Grid>
                )}
                {currentProduct.dimensions && (
                  <Grid size={6}>
                    <Typography variant="body2" color="text.secondary">
                      Dimensions: {currentProduct.dimensions}
                    </Typography>
                  </Grid>
                )}
                {currentProduct.color && (
                  <Grid size={6}>
                    <Typography variant="body2" color="text.secondary">
                      Color: {currentProduct.color}
                    </Typography>
                  </Grid>
                )}
                {currentProduct.material && (
                  <Grid size={6}>
                    <Typography variant="body2" color="text.secondary">
                      Material: {currentProduct.material}
                    </Typography>
                  </Grid>
                )}
                <Grid size={6}>
                  <Typography variant="body2" color="text.secondary">
                    SKU: {currentProduct.sku}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Quantity and Add to Cart */}
            {!isOutOfStock && (
              <Paper elevation={1} sx={{ p: 3 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Typography variant="h6">Quantity:</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 1}
                    >
                      <RemoveIcon />
                    </IconButton>
                    <Typography
                      variant="h6"
                      sx={{ minWidth: 40, textAlign: "center" }}
                    >
                      {quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(quantity + 1)}
                      disabled={quantity >= currentProduct.stockQuantity}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={<ShoppingCartIcon />}
                  onClick={handleAddToCart}
                  sx={{ mb: 2 }}
                >
                  Add to Cart - ₱
                  {(currentProduct.price * quantity).toLocaleString()}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    handleAddToCart();
                    navigate("/cart");
                  }}
                >
                  Buy Now
                </Button>
              </Paper>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Product Tabs */}
      <Box sx={{ mt: 6 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="product information tabs"
            variant="fullWidth"
          >
            <Tab label="Description" {...a11yProps(0)} />
            <Tab label="Reviews" {...a11yProps(1)} />
            <Tab label="Shipping & Returns" {...a11yProps(2)} />
            <Tab label="FAQ" {...a11yProps(3)} />
          </Tabs>
        </Box>

        {/* Description Tab */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h5" gutterBottom>
            Product Description
          </Typography>
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.8,
              mb: 3,
              whiteSpace: "pre-line",
            }}
          >
            {currentProduct.detailedDescription || currentProduct.description}
          </Typography>

          {/* Additional product specifications */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Specifications
          </Typography>
          <Grid container spacing={2}>
            {currentProduct.brand && (
              <Grid size={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Brand:</strong> {currentProduct.brand}
                </Typography>
              </Grid>
            )}
            {currentProduct.dimensions && (
              <Grid size={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Dimensions:</strong> {currentProduct.dimensions}
                </Typography>
              </Grid>
            )}
            {currentProduct.color && (
              <Grid size={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Color:</strong> {currentProduct.color}
                </Typography>
              </Grid>
            )}
            {currentProduct.material && (
              <Grid size={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Material:</strong> {currentProduct.material}
                </Typography>
              </Grid>
            )}
            {currentProduct.weight && (
              <Grid size={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Weight:</strong> {currentProduct.weight}kg
                </Typography>
              </Grid>
            )}
            <Grid size={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>SKU:</strong> {currentProduct.sku}
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Reviews Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h5" gutterBottom>
            Customer Reviews
          </Typography>

          {reviewsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Review Summary */}
              {reviewSummary && (
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid size={4}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h3" color="primary">
                          {reviewSummary.averageRating.toFixed(1)}
                        </Typography>
                        <Rating
                          value={reviewSummary.averageRating}
                          readOnly
                          precision={0.1}
                          size="large"
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Based on {reviewSummary.totalReviews} reviews
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={8}>
                      {[5, 4, 3, 2, 1].map((rating) => (
                        <Box
                          key={rating}
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <Typography variant="body2" sx={{ minWidth: 20 }}>
                            {rating}
                          </Typography>
                          <Rating
                            value={1}
                            max={1}
                            readOnly
                            size="small"
                            sx={{ mx: 1 }}
                          />
                          <Box
                            sx={{
                              flex: 1,
                              height: 8,
                              backgroundColor: "grey.200",
                              borderRadius: 1,
                              mx: 1,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                height: "100%",
                                backgroundColor: "primary.main",
                                width: `${
                                  ((reviewSummary.ratingDistribution[rating] ||
                                    0) /
                                    reviewSummary.totalReviews) *
                                  100
                                }%`,
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ minWidth: 30 }}>
                            {reviewSummary.ratingDistribution[rating] || 0}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Individual Reviews */}
              {reviews.length > 0 ? (
                <Stack spacing={2}>
                  {reviews.map((review) => (
                    <Paper key={review.id} elevation={1} sx={{ p: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Avatar sx={{ mr: 2 }}>
                          {review.userName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight="bold">
                              {review.userName}
                            </Typography>
                            {review.verified && (
                              <Chip
                                icon={<VerifiedIcon />}
                                label="Verified Purchase"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Rating
                              value={review.rating}
                              readOnly
                              size="small"
                            />
                            <Typography variant="body2" color="text.secondary">
                              {new Date(review.createdAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {review.title && review.title.trim() !== "" && (
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ mb: 1 }}
                        >
                          {review.title}
                        </Typography>
                      )}

                      {review.comment && (
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                          {review.comment}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 4 }}
                >
                  No reviews yet. Be the first to review this product!
                </Typography>
              )}
            </>
          )}
        </TabPanel>

        {/* Shipping & Returns Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h5" gutterBottom>
            Shipping & Returns
          </Typography>

          <Grid container spacing={4}>
            <Grid size={6}>
              <Typography variant="h6" gutterBottom>
                Shipping Information
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Free Shipping"
                    secondary="Free delivery for orders over ₱5,000 within Metro Manila"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Standard Delivery"
                    secondary="₱200 delivery fee, 3-5 business days"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Express Delivery"
                    secondary="₱500 delivery fee, 1-2 business days"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Provincial Delivery"
                    secondary="₱300-800 delivery fee, 5-10 business days depending on location"
                  />
                </ListItem>
              </List>
            </Grid>

            <Grid size={6}>
              <Typography variant="h6" gutterBottom>
                Return Policy
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="30-Day Return Policy"
                    secondary="Return items within 30 days of delivery for a full refund"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Original Condition"
                    secondary="Items must be in original condition with all packaging"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Free Return Pickup"
                    secondary="We'll arrange free pickup for defective or damaged items"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Refund Processing"
                    secondary="Refunds processed within 7-10 business days after return"
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </TabPanel>

        {/* FAQ Tab */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h5" gutterBottom>
            Frequently Asked Questions
          </Typography>

          <Stack spacing={2}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  What payment methods do you accept?
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  We accept credit cards (Visa, Mastercard), debit cards, GCash,
                  PayMaya, bank transfers, and cash on delivery (COD) for select
                  areas.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Is assembly required?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Most of our furniture comes partially assembled. Simple
                  assembly instructions and tools are included. Professional
                  assembly service is available for an additional fee.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Do you provide warranty?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Yes, all our furniture comes with a 1-year manufacturer
                  warranty covering defects in materials and workmanship.
                  Extended warranty options are available.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Can I track my order?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Yes, once your order is shipped, you'll receive a tracking
                  number via email and SMS. You can also track your order status
                  in your account dashboard.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  What if my item arrives damaged?
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  If your item arrives damaged, please contact us within 48
                  hours with photos of the damage. We'll arrange for a
                  replacement or full refund at no cost to you.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  Do you deliver to all areas in the Philippines?
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  We deliver to most areas in the Philippines. Delivery fees and
                  timeframes vary by location. Please check delivery options
                  during checkout for your specific area.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </TabPanel>
      </Box>
    </Container>
  );
};

export default ProductDetailPage;
